import { useState, useEffect, useCallback } from 'react'
import { casosApi } from '../api/casos'
import Boton from '../components/Boton'
import LinkDoc from '../components/LinkDoc'
import { useStoredState } from '../hooks/useStoredState'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useFilaLink } from '../hooks/useFilaLink'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtFecha, fmtMonto } from '../utils/fecha'

/*
 * Casos de entrega — reposiciones, DEVOLUCIONES por producto defectuoso
 * (Martin, 2 oct 2026) y FRAUDES detectados en tránsito (31 oct 2026). La columna "Próximo paso" dice de quién es la pelota
 * (Logística, Depósito, Administración, Vendedor): la arma el backend.
 */

const DEFAULTS = { estado: 'abierto', tipo: '' }

const TIPO = {
  devolucion: { label: 'Devolución', bg: colors.infoBg, color: colors.info },
  reposicion: { label: 'Reposición', bg: colors.zonaBg, color: colors.zonaText },
  fraude: { label: 'Fraude', bg: colors.errBg, color: colors.err },
}

// En qué está cada caso: la frase la arma el backend (estado_caso), acá solo el color.
const TONO = {
  ok: { background: colors.okBg, color: colors.ok },
  warn: { background: colors.warnBg, color: colors.warn },
  info: { background: colors.infoBg, color: colors.info },
}

function FilaCaso({ c, onCerrado }) {
  const destino = ['devolucion', 'fraude'].includes(c.tipo) ? `/casos/${c.id}` : `/logistica/remitos/${c.remito?.id}`
  const link = useFilaLink(destino)
  const [cerrando, setCerrando] = useState(false)
  // Una reposición se cierra a mano (la que entrega una salida o Andreani se
  // cierra sola). Las devoluciones y los fraudes se cierran solos al completar los pasos.
  const cerrable = c.tipo === 'reposicion' && c.estado === 'abierto'
  const cerrar = async () => {
    if (!window.confirm(`¿Cerrar el caso #${c.id}?`)) return
    setCerrando(true)
    try {
      await casosApi.resolver(c.id)
      onCerrado?.()
    } catch (e) {
      window.alert(e.response?.data?.message || 'No se pudo cerrar el caso.')
      setCerrando(false)
    }
  }
  const tipo = TIPO[c.tipo] || TIPO.reposicion

  return (
    <tr {...link} style={st.tr} title="Abrir el caso"
      onMouseEnter={e => { e.currentTarget.style.background = colors.bgSoft }}
      onMouseLeave={e => { e.currentTarget.style.background = '' }}>
      <td style={{ ...sx.td, ...sx.mono, color: colors.textSec, fontWeight: 600, whiteSpace: 'nowrap' }}>
        <LinkDoc to={destino}>#{c.id}</LinkDoc>
        <div style={{ fontSize: text.sec, fontWeight: 400, color: colors.textMeta }}>{fmtFecha(c.created_at)}</div>
      </td>
      <td style={sx.td}><span style={{ ...sx.badge, background: tipo.bg, color: tipo.color }}>{tipo.label}</span></td>
      <td style={sx.td}>
        <div style={st.principal}>{c.cliente || '—'}</div>
        <div style={st.sub}>{c.detalle}</div>
      </td>
      <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{c.remito?.numero_presea || <span style={sx.placeholder}>—</span>}</td>
      <td style={{ ...sx.td, ...sx.mono, fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{c.importe != null ? `$ ${fmtMonto(c.importe)}` : '—'}</td>
      <td style={{ ...sx.td, minWidth: 160 }}>
        {c.estado_caso && <span style={{ ...sx.badge, ...TONO[c.estado_caso.tono], display: 'inline-block', whiteSpace: 'normal' }}>{c.estado_caso.etiqueta}</span>}
        {c.estado === 'abierto' && c.proximo_paso?.quien && <div style={st.sub}>Lo tiene {c.proximo_paso.quien}</div>}
      </td>
      <td style={sx.td} onClick={e => e.stopPropagation()}>
        {cerrable && (
          <Boton variante={c.estado_caso?.codigo === 'reposicion_entregada' ? 'success' : 'secondary'} sm disabled={cerrando} onClick={cerrar} title="Cerrar el caso">
            {cerrando ? 'Cerrando…' : '✓ Cerrar'}
          </Boton>
        )}
      </td>
    </tr>
  )
}

export default function CasosPage() {
  const [filtros, setFiltros] = useStoredState('lgr.filtros.casos', DEFAULTS)
  const [casos, setCasos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const cargar = useCallback(async ({ silent } = {}) => {
    if (!silent) setCargando(true)
    try {
      const { data } = await casosApi.listar({ estado: filtros.estado, tipo: filtros.tipo || undefined })
      setCasos(data); setError('')
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudieron cargar los casos.')
    } finally { if (!silent) setCargando(false) }
  }, [filtros.estado, filtros.tipo])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  const hayFiltros = filtros.estado !== DEFAULTS.estado || filtros.tipo !== DEFAULTS.tipo

  return (
    <div style={st.wrap}>
      <div style={st.header}>
        <h2 style={st.title}>Casos de entrega</h2>
        <span style={st.bajada}>Reposiciones, devoluciones por producto defectuoso y fraudes detectados en tránsito. Se abren desde el remito: ⚠️ Abrir caso (entregado) o 🚫 Fraude (no entregado).</span>
      </div>

      <div style={st.filtros}>
        <select style={st.input} value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))}>
          <option value="abierto">Abiertos</option>
          <option value="resuelto">Resueltos</option>
          <option value="todos">Todos</option>
        </select>
        <select style={st.input} value={filtros.tipo} onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))}>
          <option value="">Todos los tipos</option>
          <option value="devolucion">Solo devoluciones</option>
          <option value="fraude">Solo fraudes</option>
          <option value="reposicion">Solo reposiciones</option>
        </select>
        <Boton variante={hayFiltros ? 'secondary' : 'ghost'} onClick={() => setFiltros(DEFAULTS)}>Limpiar</Boton>
      </div>

      {error && <div style={st.errorBox}>{error}</div>}

      <div style={st.tablaWrap}>
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>Caso</th>
              <th style={sx.th}>Tipo</th>
              <th style={sx.th}>Cliente</th>
              <th style={sx.th}>Remito</th>
              <th style={{ ...sx.th, textAlign: 'right' }}>Importe</th>
              <th style={sx.th}>Estado</th>
              <th style={sx.th}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={7} style={st.vacio}>Cargando…</td></tr>
            ) : casos.length === 0 ? (
              <tr><td colSpan={7} style={st.vacio}>{filtros.estado === 'abierto' ? 'No hay casos abiertos.' : 'No hay casos.'}</td></tr>
            ) : casos.map(c => <FilaCaso key={c.id} c={c} onCerrado={() => cargar({ silent: true })} />)}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const st = {
  wrap: { maxWidth: 1200, margin: '0 auto', fontFamily: font.sans, color: colors.text },
  header: { display: 'flex', flexDirection: 'column', gap: space.xs, marginBottom: space.xl },
  bajada: { fontSize: text.sec, color: colors.textSec },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  filtros: { ...sx.filterBar, marginBottom: space.lg },
  input: { ...sx.input, background: colors.bg },
  errorBox: { background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, marginBottom: space.lg, fontSize: text.body },
  tablaWrap: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'auto' },
  tr: { height: 48, borderBottom: `1px solid ${colors.borderSoft}`, cursor: 'pointer' },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  sub: { fontSize: text.sec, color: colors.textMeta, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  vacio: { padding: space['3xl'], textAlign: 'center', color: colors.textMeta },
}
